import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@uconnect/assets/lib/utils";

const badgeVariants = cva(
	"badge inline-flex items-center justify-center rounded-full border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
	{
		variants: {
			variant: {
				default:
					"border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
				destructive:
					"border-red-300 bg-red-100 text-primary [a&]:hover:bg-red-200",
				success:
					"border-chartreuse-500 bg-chartreuse-100 text-primary [a&]:hover:bg-chartreuse-200",
				green:
					"border-green-300 bg-green-100 text-primary [a&]:hover:bg-green-200",
				warning:
					"border-yellow-300 bg-yellow-100 text-primary [a&]:hover:bg-yellow-200",
				info: "border-blue-300 bg-blue-100 text-primary [a&]:hover:bg-blue-200",
				accent: "border-lavender-300 bg-lavender-100 text-primary [a&]:hover:bg-lavender-200",
				outline:
					"text-foreground bg-white [a&]:hover:bg-primary [a&]:hover:border-primary [a&]:hover:text-primary-foreground",
				incomplete:
					"text-foreground bg-white [a&]:hover:bg-primary [a&]:hover:border-primary [a&]:hover:text-primary-foreground border-dashed",
			},
			size: {
				sm: "px-2 py-0.5 text-xs",
				default: "px-2.5 py-1 text-xs",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

function Badge({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"span"> &
	VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : "span";
	// Map aliases to canonical variants
	const VARIANTS = ["default", "destructive", "success", "green", "warning", "info", "accent", "outline", "incomplete"] as const;
	type Variant = typeof VARIANTS[number];
	const variantAliasMap: Record<string, Variant> = {
		poor: "destructive",
		fair: "warning",
		good: "success",
		excellent: "info",
		exceptional: "accent",
	};
	function resolveVariant(
		variant: unknown
	): Variant | null | undefined {
		if (typeof variant === "string") {
			if ((VARIANTS as readonly string[]).includes(variant)) return variant as Variant;
			if (variant in variantAliasMap) return variantAliasMap[variant as keyof typeof variantAliasMap];
		}
		return variant as Variant | null | undefined;
	}
	const resolvedVariant = resolveVariant(variant);

	return (
		<Comp
			data-slot="badge"
			className={cn(
				"print-colors",
				badgeVariants({ variant: resolvedVariant, size }),
				className
			)}
			{...props}
		/>
	);
}

export { Badge, badgeVariants };
